import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  companiesSelector,
  companySelector,
  optionsSelector,
  selectedSelector,
  usersSelector,
  importationsSelector,
  storesSelector,
  storeSelector,
} from './store/selector';
import { companiesApiService } from './services/api';
import {
  deleteCompanyFailuireApiAction,
  deleteCompanyInitApiAction,
  deleteCompanySuccessApiAction,
  deleteUserFailuireApiAction,
  deleteUserInitApiAction,
  deleteUserSuccessApiAction,
  getUsersFailureApiAction,
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  getCompanyFailureApiAction,
  getCompanyInitApiAction,
  getCompanySuccessApiAction,
  getCompaniesFailureApiAction,
  getCompaniesInitApiAction,
  getCompaniesSuccessApiAction,
  postCompanyFailuireApiAction,
  postCompanyInitApiAction,
  postCompanySuccessApiAction,
  postUserFailuireApiAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  companyClearAction,
  putCompanyFailuireApiAction,
  putCompanyInitApiAction,
  putCompanySuccessApiAction,
  resetOptionsAction,
  setCompanyAction,
  setOptionsAction,
  getImportationsInitApiAction,
  getImportationsSuccessApiAction,
  getImportationsFailureApiAction,
  getStoresInitApiAction,
  getStoresSuccessApiAction,
  getStoresFailureApiAction,
  postStoreInitApiAction,
  postStoreSuccessApiAction,
  postStoreFailuireApiAction,
  putStoreInitApiAction,
  putStoreSuccessApiAction,
  putStoreFailuireApiAction,
  deleteStoreInitApiAction,
  deleteStoreSuccessApiAction,
  deleteStoreFailuireApiAction,
  setStoreAction,
  postUserStoreInitApiAction,
  postUserStoreFailuireApiAction,
  deleteUserStoreInitApiAction,
  deleteUserStoreFailuireApiAction,
} from './store/reducer';
import { PostCompanyPayloadRequest, PostCompanyResponse } from './interfaces/post-company';
import { GetCompaniesParams } from './interfaces/get-companies';
import { PutCompanyPayloadRequest } from './interfaces/put-company';
import { PostUserPayloadRequest } from './interfaces/post-user';
import { Company } from '../../interfaces/company.interface';
import { PostStorePayloadRequest } from './interfaces/post-store';
import { PutStorePayloadRequest } from './interfaces/put-store';
import { Store } from '../../interfaces/store.interface';

export function useCompanyCore() {
  const companies = useSelector(companiesSelector);
  const company = useSelector(companySelector);
  const selected = useSelector(selectedSelector);
  const options = useSelector(optionsSelector);
  const users = useSelector(usersSelector);
  const stores = useSelector(storesSelector);
  const store = useSelector(storeSelector);
  const importations = useSelector(importationsSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getMeCompanies = useCallback(
    async (params: GetCompaniesParams, onSuccess?: () => void) => {
      try {
        dispatch(getCompaniesInitApiAction());
        const response = await companiesApiService.getMeCompanies(params);
        dispatch(getCompaniesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getCompaniesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getCompanies = useCallback(
    async (params: GetCompaniesParams,  onSuccess?: () => void) => {
      try {
        dispatch(getCompaniesInitApiAction());
        const response = await companiesApiService.getCompanies(params);
        dispatch(getCompaniesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getCompaniesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getCompany = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getCompanyInitApiAction());
        const response = await companiesApiService.getCompany(id);
        dispatch(getCompanySuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getCompanyFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getMeCompany = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getCompanyInitApiAction());
        const response = await companiesApiService.getMeCompany(id);
        dispatch(getCompanySuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getCompanyFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postCompany = useCallback(
    async (
      body: PostCompanyPayloadRequest,
      onSuccess?: (response: PostCompanyResponse) => void,
      onFailure?: (error: any) => void
    ) => {
      try {
        dispatch(postCompanyInitApiAction());
        const response = await companiesApiService.postCompany(body);
        dispatch(postCompanySuccessApiAction());
        onSuccess?.(response);
      } catch (error: any) {
        console.error(error);
        onFailure?.(error);
        dispatch(postCompanyFailuireApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const putCompany = useCallback(
    async (
      id: string,
      body: PutCompanyPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putCompanyInitApiAction());
        await companiesApiService.putCompany(id, body);
        dispatch(putCompanySuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(putCompanyFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteCompany = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(deleteCompanyInitApiAction());
        await companiesApiService.deleteCompany(id);
        dispatch(deleteCompanySuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(deleteCompanyFailuireApiAction());
      }
    },
    [dispatch]
  );

  const getUsers = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getUsersInitApiAction());
        const response = await companiesApiService.getUsers(id);
        dispatch(getUsersSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getUsersFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postUser = useCallback(
    async (
      id: string,
      body: PostUserPayloadRequest,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postUserInitApiAction());
        await companiesApiService.postUser(id, body);
        dispatch(postUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteUser = useCallback(
    async (id: string, uid: string, onSuccess?: () => void) => {
      try {
        dispatch(deleteUserInitApiAction());
        await companiesApiService.deleteUser(id, uid);
        dispatch(deleteUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(deleteUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const getStores = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getStoresInitApiAction());
        const response = await companiesApiService.getStores(id);
        dispatch(getStoresSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getStoresFailureApiAction({ errorCode: error.request.status })
        );
        throw error;
      }
    },
    [dispatch]
  );

  const postStore = useCallback(
    async (id: string, body: PostStorePayloadRequest, onSuccess?: () => void) => {
      try {
        dispatch(postStoreInitApiAction());
        await companiesApiService.postStore(id, body);
        dispatch(postStoreSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postStoreFailuireApiAction());
        throw error;
      }
    },
    [dispatch]
  );

  const putStore = useCallback(
    async (
      id: string,
      sid: string,
      body: PutStorePayloadRequest,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putStoreInitApiAction());
        await companiesApiService.putStore(id, sid, body);
        dispatch(putStoreSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(putStoreFailuireApiAction());
        throw error;
      }
    },
    [dispatch]
  );

const deleteStore = useCallback(
  async (
    id: string,
    sid: string,
    onSuccess?: () => void,
    onFailure?: (error: any) => void
  ) => {
    try {
      dispatch(deleteStoreInitApiAction());
      await companiesApiService.deleteStore(id, sid);
      dispatch(deleteStoreSuccessApiAction());
      onSuccess?.();
    } catch (error: any) {
      console.error(error);
      onFailure?.(error);
      
      dispatch(deleteStoreFailuireApiAction());
    }
  },
  [dispatch]
);

  const getImportations = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getImportationsInitApiAction());
        const response = await companiesApiService.getImportations(id);
        dispatch(getImportationsSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getImportationsFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const selectCompany = useCallback((project: Company) => {
    dispatch(setCompanyAction(project))
  }, [dispatch]);

  const setStore = useCallback((store: Store | null) => {
    dispatch(setStoreAction(store));
  }, [dispatch]);

  const setOptions = useCallback((options: GetCompaniesParams) => {
    dispatch(setOptionsAction(options))
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const clearCompany = useCallback(() => {
    dispatch(companyClearAction());
  }, [dispatch]);

  return {
    companies,
    company,
    selected,
    users,
    stores,
    store,
    importations,
    options,
    errorCode,
    getMeCompanies,
    getCompanies,
    getCompany,
    getMeCompany,
    getUsers,
    postCompany,
    putCompany,
    deleteCompany,
    postUser,
    deleteUser,
    getStores,
    postStore,
    putStore,
    deleteStore,
    setStore,
    getImportations,
    selectCompany,
    setOptions,
    resetOptions,
    clearCompany,
  };
}
